import React, { useState, useMemo } from 'react';
import { TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, Loader2, ChevronDown, TrendingDown, Minus } from 'lucide-react';
import { useRentGrowthRankings, useRentGrowthSummary } from '../../hooks/useRentGrowth';
import type { SubmarketRentGrowth } from '../../types/rentGrowth';
import { SectionTitle } from '../Luxury';

type SortKey = 'composite_score' | 'cagr_5y' | 'accel_delta' | 'vacancy_rate' | 'permit_density' | 'name';
type SortDir = 'asc' | 'desc';

const signalStyles: Record<string, string> = {
  BUY: 'bg-emerald/20 text-emerald border-emerald/40',
  HOLD: 'bg-gold/20 text-gold border-gold/40',
  WAIT: 'bg-crimson/20 text-crimson border-crimson/40',
};

function SignalBadge({ signal }: { signal: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${signalStyles[signal] || signalStyles.HOLD}`}
    >
      {signal}
    </span>
  );
}

function formatNum(val: number | null | undefined, decimals = 1): string {
  if (val === null || val === undefined) return '--';
  return val.toFixed(decimals);
}

function formatDollar(val: number | null | undefined): string {
  if (val === null || val === undefined) return '--';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function CagrPotentialBadge({ cagr }: { cagr: number | null }) {
  if (cagr === null || cagr === undefined) return <span className="text-stone-500">--</span>;
  const color = cagr >= 5 ? 'text-emerald bg-emerald/10' : cagr >= 3 ? 'text-gold bg-gold/10' : 'text-crimson bg-crimson/10';
  const label = cagr >= 5 ? 'Strong' : cagr >= 3 ? 'Moderate' : 'Weak';
  return (
    <div className="flex items-center gap-2">
      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${color}`}>
        {label}
      </span>
      <span className="text-sm font-mono text-ivory">{cagr.toFixed(1)}%</span>
    </div>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 text-stone-600" />;
  return dir === 'asc'
    ? <ArrowUp className="w-3 h-3 text-gold" />
    : <ArrowDown className="w-3 h-3 text-gold" />;
}

interface RentGrowthRankingsTableProps {
  marketId?: string;
}

/**
 * Sortable rankings table for all 29 submarkets, used on the ZandiMapPage.
 * Enhanced with rent growth potential column, zebra striping, and signal filters.
 */
export function RentGrowthRankingsTable({ marketId }: RentGrowthRankingsTableProps) {
  const { data, isLoading, error } = useRentGrowthRankings();
  const { data: summary } = useRentGrowthSummary();
  const [sortKey, setSortKey] = useState<SortKey>('composite_score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [signalFilter, setSignalFilter] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const sorted = useMemo(() => {
    if (!data?.rankings) return [];
    let list = [...data.rankings];
    if (signalFilter) {
      list = list.filter((r) => r.signal === signalFilter);
    }
    list.sort((a, b) => {
      const aVal = a[sortKey] ?? -Infinity;
      const bVal = b[sortKey] ?? -Infinity;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
    return list;
  }, [data, sortKey, sortDir, signalFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Graceful empty state for non-Philadelphia markets
  if (marketId && marketId !== 'philadelphia') {
    return (
      <div className="bg-charcoal border border-slate-dark rounded-lg p-8 text-center">
        <SectionTitle icon={<TrendingUp className="w-5 h-5" />}>
          Rent Growth Rankings
        </SectionTitle>
        <p className="text-stone-500 text-sm">
          Rent growth intelligence is not yet available for this market. This data is currently Philadelphia-only.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-charcoal border border-slate-dark rounded-lg p-8 text-center">
        <Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" />
        <p className="text-stone-400 mt-2 text-sm">Loading rent growth data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-charcoal border border-slate-dark rounded-lg p-8 text-center">
        <p className="text-crimson text-sm">Failed to load rent growth rankings</p>
      </div>
    );
  }

  const headerClass =
    'px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-stone-400 cursor-pointer hover:text-ivory transition-colors select-none';

  return (
    <div className="bg-charcoal border border-slate-dark rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-dark">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <SectionTitle icon={<TrendingUp className="w-5 h-5" />}>
            Rent Growth Rankings
          </SectionTitle>
          <div className="flex items-center gap-4">
            {/* Summary badges */}
            {summary && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-stone-400">Avg Score:</span>
                <span className="text-ivory font-mono font-medium">{summary.avg_composite.toFixed(1)}</span>
                <span className="text-stone-500 mx-1">|</span>
                <span className="text-stone-400">Median 5Y:</span>
                <span className="text-ivory font-mono font-medium">{summary.median_5y_cagr.toFixed(1)}%</span>
              </div>
            )}
            {/* Signal filter buttons */}
            <div className="flex items-center gap-1">
              {['BUY', 'HOLD', 'WAIT'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSignalFilter(signalFilter === s ? null : s)}
                  className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-semibold border transition-all ${
                    signalFilter === s
                      ? signalStyles[s]
                      : 'border-slate-dark text-stone-500 hover:text-stone-300'
                  }`}
                >
                  {s} {summary && (s === 'BUY' ? summary.buy_count : s === 'HOLD' ? summary.hold_count : summary.wait_count)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-dark bg-graphite/50">
              <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-stone-400 w-10">
                #
              </th>
              <th className={headerClass} onClick={() => toggleSort('name')}>
                <span className="flex items-center gap-1">
                  Submarket <SortIcon active={sortKey === 'name'} dir={sortDir} />
                </span>
              </th>
              <th className="px-3 py-2.5 text-center text-[11px] uppercase tracking-wider font-semibold text-stone-400">
                Signal
              </th>
              <th className={headerClass} onClick={() => toggleSort('composite_score')}>
                <span className="flex items-center gap-1">
                  Composite <SortIcon active={sortKey === 'composite_score'} dir={sortDir} />
                </span>
              </th>
              <th className={headerClass} onClick={() => toggleSort('cagr_5y')}>
                <span className="flex items-center gap-1">
                  Rent Growth Potential <SortIcon active={sortKey === 'cagr_5y'} dir={sortDir} />
                </span>
              </th>
              <th className={headerClass} onClick={() => toggleSort('accel_delta')}>
                <span className="flex items-center gap-1">
                  Accel. <SortIcon active={sortKey === 'accel_delta'} dir={sortDir} />
                </span>
              </th>
              <th className={headerClass} onClick={() => toggleSort('vacancy_rate')}>
                <span className="flex items-center gap-1">
                  Vacancy <SortIcon active={sortKey === 'vacancy_rate'} dir={sortDir} />
                </span>
              </th>
              <th className={headerClass} onClick={() => toggleSort('permit_density')}>
                <span className="flex items-center gap-1">
                  Permits <SortIcon active={sortKey === 'permit_density'} dir={sortDir} />
                </span>
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-semibold text-stone-400">
                EGI
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row: SubmarketRentGrowth, idx: number) => {
              const isExpanded = expandedRow === row.name;
              const topDrivers = row.drivers?.slice(0, 3) || [];
              return (
                <React.Fragment key={row.name}>
                  <tr
                    onClick={() => setExpandedRow(isExpanded ? null : row.name)}
                    className={`border-b border-slate-dark/50 cursor-pointer transition-colors ${
                      idx % 2 === 0 ? 'bg-charcoal' : 'bg-graphite/20'
                    } hover:bg-graphite/40 ${isExpanded ? 'bg-graphite/40' : ''}`}
                  >
                    <td className="px-3 py-2.5 text-stone-500 text-sm font-mono">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-medium max-w-[200px] truncate">
                      <span className="flex items-center gap-2 text-ivory">
                        {row.name}
                        <ChevronDown className={`w-3 h-3 text-stone-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <SignalBadge signal={row.signal} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-graphite rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(row.composite_score, 100)}%`,
                              backgroundColor:
                                row.composite_score >= 60
                                  ? '#10b981'
                                  : row.composite_score >= 35
                                    ? '#d4a853'
                                    : '#ef4444',
                            }}
                          />
                        </div>
                        <span className="text-ivory text-sm font-mono w-10 text-right">
                          {row.composite_score.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <CagrPotentialBadge cagr={row.cagr_5y} />
                    </td>
                    <td className="px-3 py-2.5">
                      {row.accel_delta === null ? (
                        <span className="text-stone-500 text-sm font-mono">--</span>
                      ) : row.accel_delta > 0 ? (
                        <span className="flex items-center justify-end gap-1 text-emerald">
                          <TrendingUp className="w-3 h-3 flex-shrink-0" />
                          <span className="text-sm font-mono">+{formatNum(row.accel_delta)}</span>
                        </span>
                      ) : row.accel_delta < -1 ? (
                        <span className="flex items-center justify-end gap-1 text-crimson">
                          <TrendingDown className="w-3 h-3 flex-shrink-0" />
                          <span className="text-sm font-mono">{formatNum(row.accel_delta)}</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-end gap-1 text-stone-400">
                          <Minus className="w-3 h-3 flex-shrink-0" />
                          <span className="text-sm font-mono">{formatNum(row.accel_delta)}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-ivory">
                      {formatNum(row.vacancy_rate)}%
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-ivory">
                      {formatNum(row.permit_density, 2)}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-ivory">
                      {formatDollar(row.egi_latest)}
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && topDrivers.length > 0 && (
                    <tr className="bg-graphite/30">
                      <td colSpan={9} className="px-6 py-3">
                        <div className="grid grid-cols-3 gap-4">
                          {topDrivers.map((driver) => {
                            const pct = driver.contribution_pct ?? 0;
                            return (
                              <div key={driver.name} className="flex items-center gap-2">
                                <span className="text-xs text-stone-400 w-24 truncate">{driver.name}</span>
                                <div className="flex-1 h-1.5 bg-graphite rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gold/60"
                                    style={{ width: `${Math.min(driver.normalized_score || 0, 100)}%` }}
                                  />
                                </div>
                                <span className="text-[10px] font-mono text-stone-500 w-8 text-right">{pct.toFixed(0)}%</span>
                              </div>
                            );
                          })}
                        </div>
                        {row.signal_rationale && (
                          <p className="text-xs text-stone-500 mt-2 italic">{row.signal_rationale}</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-slate-dark bg-graphite/30">
        <p className="text-stone-500 text-xs">
          {data?.data_source} | {sorted.length} of {data?.total ?? 0} submarkets
        </p>
      </div>
    </div>
  );
}
